/**
 * File: assets/basari-plp.js
 * Purpose: PLP filter UX — drawer, checkbox submit, price validation,
 *   desktop dual-thumb price slider sync, accordion memory.
 * Sprint: 2 / 6C price filter
 * Notes:
 *   - Native GET forms (no Dawn facets.js / ProductGridContainer AJAX).
 *   - Progressive enhancement only.
 */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var DRAWER_MS = 280;

  function getFocusable(container) {
    return Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(function (node) {
      return (
        !node.hasAttribute('disabled') &&
        node.getAttribute('aria-hidden') !== 'true' &&
        node.offsetParent !== null
      );
    });
  }

  function lockScroll(lock) {
    document.documentElement.classList.toggle('basari-plp-drawer-open', lock);
    document.body.classList.toggle('basari-plp-drawer-open', lock);
  }

  function parsePrice(value) {
    if (value == null || value === '') return null;
    var normalized = String(value).replace(/[^\d.,-]/g, '').replace(',', '.');
    var number = parseFloat(normalized);
    return isNaN(number) ? null : number;
  }

  function formatPrice(value) {
    if (value == null || isNaN(value)) return '';
    return String(Math.round(value * 100) / 100);
  }

  /* --------------------------------------------------------------------------
     Filter drawer
     -------------------------------------------------------------------------- */
  function initDrawer(root) {
    var drawer = root.querySelector('[data-basari-plp-drawer]');
    if (!drawer) return;

    var panel = drawer.querySelector('[data-basari-plp-drawer-panel]');
    var overlay = drawer.querySelector('[data-basari-plp-drawer-overlay]');
    var closeBtn = drawer.querySelector('[data-basari-plp-drawer-close]');
    var openBtns = root.querySelectorAll('[data-basari-plp-filter-open]');
    var lastFocus = null;
    var closing = false;

    if (panel && !panel.hasAttribute('tabindex')) {
      panel.setAttribute('tabindex', '-1');
    }

    function setExpanded(expanded) {
      openBtns.forEach(function (btn) {
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    function openDrawer() {
      if (drawer.classList.contains('is-open')) return;
      closing = false;
      lastFocus = document.activeElement;
      drawer.hidden = false;
      lockScroll(true);
      setExpanded(true);

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          drawer.classList.add('is-open');
          var focusables = getFocusable(panel);
          (focusables[0] || panel).focus();
        });
      });
    }

    function closeDrawer() {
      if (!drawer.classList.contains('is-open') || closing) return;
      closing = true;
      drawer.classList.remove('is-open');
      lockScroll(false);
      setExpanded(false);

      var settled = false;
      function finish() {
        if (settled) return;
        settled = true;
        drawer.hidden = true;
        closing = false;
        if (lastFocus && typeof lastFocus.focus === 'function') {
          lastFocus.focus();
        }
      }

      function onTransitionEnd(event) {
        if (event.target !== panel) return;
        panel.removeEventListener('transitionend', onTransitionEnd);
        finish();
      }

      panel.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(finish, DRAWER_MS);
    }

    openBtns.forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        openDrawer();
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (event) {
      if (!drawer.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (event.key !== 'Tab' || !panel) return;

      var focusables = getFocusable(panel);
      if (!focusables.length) {
        event.preventDefault();
        panel.focus();
        return;
      }

      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* --------------------------------------------------------------------------
     Price validation + checkbox auto-submit
     -------------------------------------------------------------------------- */
  function validatePriceForm(form) {
    var minInput = form.querySelector('[data-basari-plp-price-min]');
    var maxInput = form.querySelector('[data-basari-plp-price-max]');
    if (!minInput && !maxInput) return true;

    var rangeMax = parsePrice(
      (minInput && minInput.getAttribute('data-max')) ||
        (maxInput && maxInput.getAttribute('data-max')) ||
        ''
    );
    var minVal = minInput ? parsePrice(minInput.value) : null;
    var maxVal = maxInput ? parsePrice(maxInput.value) : null;

    if (minInput && minVal != null) {
      if (minVal < 0) minVal = 0;
      if (rangeMax != null && minVal > rangeMax) minVal = rangeMax;
      minInput.value = formatPrice(minVal);
    }

    if (maxInput && maxVal != null) {
      if (maxVal < 0) maxVal = 0;
      if (rangeMax != null && maxVal > rangeMax) maxVal = rangeMax;
      maxInput.value = formatPrice(maxVal);
    }

    minVal = minInput ? parsePrice(minInput.value) : null;
    maxVal = maxInput ? parsePrice(maxInput.value) : null;

    if (minVal != null && maxVal != null && minVal > maxVal) {
      if (maxInput) maxInput.value = formatPrice(minVal);
    }

    return true;
  }

  function initFilterForms(root) {
    root.querySelectorAll('[data-basari-plp-filters-form]').forEach(function (form) {
      form.addEventListener('change', function (event) {
        var target = event.target;
        if (!target || target.type !== 'checkbox') return;
        validatePriceForm(form);
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      });

      form.addEventListener('submit', function () {
        validatePriceForm(form);
      });
    });
  }

  /* --------------------------------------------------------------------------
     Desktop dual-thumb price slider ↔ S&D min/max inputs + summary
     -------------------------------------------------------------------------- */
  function initPriceSliders(root) {
    root.querySelectorAll('[data-basari-plp-price]').forEach(function (wrap) {
      var sliderMin = wrap.querySelector('[data-basari-plp-price-slider-min]');
      var sliderMax = wrap.querySelector('[data-basari-plp-price-slider-max]');
      var rangeEl = wrap.querySelector('[data-basari-plp-price-slider-range]');
      var minInput = wrap.querySelector('[data-basari-plp-price-min]');
      var maxInput = wrap.querySelector('[data-basari-plp-price-max]');
      var summaryMin = wrap.querySelector('[data-basari-plp-price-summary-min]');
      var summaryMax = wrap.querySelector('[data-basari-plp-price-summary-max]');
      if (!sliderMin || !sliderMax || !minInput || !maxInput) return;

      var symbol = wrap.getAttribute('data-currency-symbol') || '';
      var rangeMax =
        parsePrice(wrap.getAttribute('data-range-max')) ||
        parsePrice(sliderMax.getAttribute('max')) ||
        0;

      function clamp(value, min, max) {
        if (value == null || isNaN(value)) return min;
        return Math.min(max, Math.max(min, value));
      }

      function paintRange(minVal, maxVal) {
        if (!rangeEl || rangeMax <= 0) return;
        var left = (minVal / rangeMax) * 100;
        var right = (maxVal / rangeMax) * 100;
        rangeEl.style.left = left + '%';
        rangeEl.style.width = Math.max(0, right - left) + '%';
      }

      function updateSummary(minVal, maxVal) {
        if (summaryMin) summaryMin.textContent = symbol + formatPrice(minVal);
        if (summaryMax) summaryMax.textContent = symbol + formatPrice(maxVal);
      }

      function applyPair(minVal, maxVal, source) {
        minVal = clamp(minVal, 0, rangeMax);
        maxVal = clamp(maxVal, 0, rangeMax);
        if (minVal > maxVal) {
          if (source === 'min') maxVal = minVal;
          else minVal = maxVal;
        }

        if (source !== 'min-text') minInput.value = formatPrice(minVal);
        if (source !== 'max-text') maxInput.value = formatPrice(maxVal);
        if (source !== 'min-slider') sliderMin.value = String(minVal);
        if (source !== 'max-slider') sliderMax.value = String(maxVal);

        sliderMin.setAttribute('aria-valuenow', String(minVal));
        sliderMax.setAttribute('aria-valuenow', String(maxVal));
        paintRange(minVal, maxVal);
        updateSummary(minVal, maxVal);
      }

      function readMin() {
        return clamp(parsePrice(minInput.value) != null ? parsePrice(minInput.value) : parsePrice(sliderMin.value), 0, rangeMax);
      }

      function readMax() {
        var parsed = parsePrice(maxInput.value);
        if (parsed == null) parsed = parsePrice(sliderMax.value);
        if (parsed == null) parsed = rangeMax;
        return clamp(parsed, 0, rangeMax);
      }

      applyPair(readMin(), readMax(), 'init');

      sliderMin.addEventListener('input', function () {
        var minVal = parsePrice(sliderMin.value) || 0;
        var maxVal = parsePrice(sliderMax.value);
        if (maxVal == null) maxVal = rangeMax;
        if (minVal > maxVal) {
          maxVal = minVal;
          sliderMax.value = String(maxVal);
        }
        applyPair(minVal, maxVal, 'min-slider');
      });

      sliderMax.addEventListener('input', function () {
        var minVal = parsePrice(sliderMin.value) || 0;
        var maxVal = parsePrice(sliderMax.value);
        if (maxVal == null) maxVal = rangeMax;
        if (maxVal < minVal) {
          minVal = maxVal;
          sliderMin.value = String(minVal);
        }
        applyPair(minVal, maxVal, 'max-slider');
      });

      minInput.addEventListener('input', function () {
        applyPair(readMin(), readMax(), 'min-text');
      });
      maxInput.addEventListener('input', function () {
        applyPair(readMin(), readMax(), 'max-text');
      });
      minInput.addEventListener('change', function () {
        applyPair(readMin(), readMax(), 'min-text');
      });
      maxInput.addEventListener('change', function () {
        applyPair(readMin(), readMax(), 'max-text');
      });
    });
  }

  /* --------------------------------------------------------------------------
     Accordion open-state memory + aria-expanded sync
     -------------------------------------------------------------------------- */
  function initAccordionMemory(root) {
    var storageKey = 'basari-plp-filter-open:' + (root.getAttribute('data-basari-plp-section') || 'default');
    var saved = {};

    try {
      saved = JSON.parse(sessionStorage.getItem(storageKey) || '{}') || {};
    } catch (e) {
      saved = {};
    }

    root.querySelectorAll('[data-basari-plp-filter-group]').forEach(function (details) {
      var param = details.getAttribute('data-filter-param');
      var summary = details.querySelector('.basari-plp-filter__summary');
      if (!param) return;

      if (Object.prototype.hasOwnProperty.call(saved, param)) {
        details.open = !!saved[param];
      }

      if (summary) {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      }

      details.addEventListener('toggle', function () {
        if (summary) {
          summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        }
        saved[param] = details.open;
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(saved));
        } catch (err) {
          /* ignore quota / private mode */
        }
      });
    });
  }

  function init(root) {
    initDrawer(root);
    initFilterForms(root);
    initPriceSliders(root);
    initAccordionMemory(root);
  }

  function boot() {
    document.querySelectorAll('[data-basari-plp]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
